import { useEffect, useState, useCallback, useRef } from 'react';
import { logger } from '../utils/logger';

export const useVersionCheck = () => {
  const [hasUpdate, setHasUpdate] = useState(false);
  const [serverVersion, setServerVersion] = useState(null);
  const currentVersionRef = useRef(null);
  const [versionLoaded, setVersionLoaded] = useState(false);

  // Carregar versão atual ao montar o componente
  useEffect(() => {
    const loadCurrentVersion = async () => {
      try {
        const response = await fetch(`/version.json?t=${Date.now()}`, {
          cache: 'no-cache',
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
          },
        });
        if (response.ok) {
          const data = await response.json();
          currentVersionRef.current = data.version;
          logger.log('[useVersionCheck] 📥 Versão inicial carregada:', data.version);
          setVersionLoaded(true);
        }
      } catch (error) {
        console.error('[useVersionCheck] ❌ Error loading current version:', error);
      }
    };
    loadCurrentVersion();
  }, []);

  // Verifica a versão
  const checkVersion = useCallback(async () => {
    // Não verificar se ainda não carregou a versão atual
    if (!currentVersionRef.current) {
      logger.log('[useVersionCheck] ⏳ Versão atual ainda não carregada, aguardando...');
      return;
    }

    try {
      logger.log('[useVersionCheck] 🔍 Verificando versão... Versão atual na memória:', currentVersionRef.current);
      
      // 🔥 OTIMIZAÇÃO: Adiciona timestamp para evitar cache do version.json
      const response = await fetch(`/version.json?t=${new Date().getTime()}`, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0',
        },
      });

      if (!response.ok) {
        logger.log('[useVersionCheck] ❌ Erro ao buscar version.json:', response.status);
        return;
      }

      const data = await response.json();
      const latestVersion = data.version;
      
      logger.log('[useVersionCheck] 📦 Versão no servidor:', latestVersion);
      logger.log('[useVersionCheck] 🔄 Comparando:', currentVersionRef.current, 'vs', latestVersion);
      
      if (latestVersion && latestVersion !== currentVersionRef.current) {
        logger.log(`[useVersionCheck] ✅ Update available: ${currentVersionRef.current} -> ${latestVersion}`);
        setServerVersion(latestVersion);
        setHasUpdate(true);
      } else if (latestVersion === currentVersionRef.current) {
        logger.log('[useVersionCheck] ℹ️ Versões iguais, sem atualização');
        // Se as versões são iguais, garantir que não há atualização pendente
        setHasUpdate(false);
        // Atualizar a versão atual caso tenha mudado (após reload)
        if (currentVersionRef.current !== latestVersion) {
          currentVersionRef.current = latestVersion;
        }
      }
    } catch (error) {
      console.error('[useVersionCheck] ❌ Error checking version:', error);
    }
  }, []);

  // ✅ OTIMIZAÇÃO: Polling a cada 15 minutos (padrão da indústria) e ao focar na janela
  // Reduzido de 5 minutos para 15 minutos para reduzir requisições
  useEffect(() => {
    // Só iniciar quando a versão atual for carregada
    if (!versionLoaded) return;
    
    // Verificar imediatamente após carregar versão
    checkVersion();
    
    // ✅ OTIMIZADO: Aumentado de 5 minutos para 15 minutos (padrão Slack/Facebook)
    const interval = setInterval(checkVersion, 15 * 60 * 1000);
    
    const onFocus = () => checkVersion();
    window.addEventListener('focus', onFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [versionLoaded, checkVersion]);

  // ✅ CORREÇÃO: Função de Reload Otimizada e Confiável
  const reloadPage = useCallback(() => {
    try {
      // Se você usa Service Workers (PWA), precisamos forçar a atualização
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then((registrations) => {
          // Desregistrar todos os service workers
          const unregisterPromises = registrations.map(registration => registration.unregister());
          Promise.all(unregisterPromises).then(() => {
            // ✅ CORREÇÃO: Reload forçado - arquivos JS/CSS já têm hash único, reload simples funciona
            window.location.reload();
          }).catch(() => {
            // Se falhar, fazer reload mesmo assim
            window.location.reload();
          });
        }).catch(() => {
          // Se falhar ao obter registrations, fazer reload direto
          window.location.reload();
        });
      } else {
        // ✅ CORREÇÃO: Reload simples - arquivos JS/CSS já têm hash único (ex: main.d34af46a.js)
        // O navegador baixará automaticamente os arquivos novos porque o hash mudou
        window.location.reload();
      }
    } catch (error) {
      console.error('Erro ao recarregar página:', error);
      // Fallback: reload simples
      window.location.reload();
    }
  }, []);

  return {
    hasUpdate,
    serverVersion,
    reloadPage
  };
};
